import { Link } from 'react-router-dom';
import { Icons } from '../components/Icons.jsx';
import { useAuth } from '../lib/auth.jsx';
import { useT } from '../lib/i18n.jsx';
import { formatDate } from '../lib/format.js';
import { subscriptionStatus } from '../lib/subscription.js';

const APP_STORE_URL = import.meta.env.VITE_APP_STORE_URL || '';
const PLAY_STORE_URL = import.meta.env.VITE_PLAY_STORE_URL || '';

function Stat({ icon, label, value, hint }) {
  const Icon = Icons[icon];
  return (
    <div className="stat">
      <span className="stat-ic" aria-hidden="true"><Icon size={18} /></span>
      <div className="stat-body">
        <small>{label}</small>
        <strong>{value}</strong>
        {hint && <span className="stat-hint">{hint}</span>}
      </div>
    </div>
  );
}

export default function Overview() {
  const t = useT();
  const { user, profiles } = useAuth();

  const sub = subscriptionStatus(user);
  const name = user?.name?.trim();

  // An account with no kid profiles has never really been used — the app
  // seeds a main profile on first run, so this is the "signed up on the web,
  // hasn't installed anything yet" case.
  const isEmpty = profiles.length === 0;

  return (
    <>
      <header className="page-head">
        <h1>{name ? t('overview.greeting', { name }) : t('overview.greetingNoName')}</h1>
      </header>

      <section className="stat-grid">
        <Stat
          icon="Crown"
          label={t('overview.subscription')}
          value={t(`subscription.${sub.state}`)}
          hint={sub.expiresAt ? formatDate(sub.expiresAt) : null}
        />
        <Stat icon="Star" label={t('overview.coins')} value={user?.coins ?? 0} />
        <Stat
          icon="Sparkle"
          label={t('overview.streak')}
          value={user?.longest_streak ?? 0}
          hint={t('overview.longestStreak', { days: user?.longest_streak ?? 0 })}
        />
        <Stat icon="Users" label={t('overview.kidsCount')} value={profiles.length} />
      </section>

      {isEmpty ? (
        <section className="card empty-card">
          <h2>{t('overview.emptyTitle')}</h2>
          <p>{t('overview.emptyBody')}</p>
          <div className="row gap">
            {APP_STORE_URL && (
              <a className="btn primary" href={APP_STORE_URL} target="_blank" rel="noreferrer">
                {t('overview.getApp')} · iOS
              </a>
            )}
            {PLAY_STORE_URL && (
              <a className="btn" href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
                {t('overview.getApp')} · Android
              </a>
            )}
          </div>
        </section>
      ) : (
        <section className="card">
          <div className="card-head">
            <h2>{t('kids.title')}</h2>
            <Link className="btn ghost" to="/kids">
              {t('overview.manageKids')}
              <Icons.Chevron size={16} />
            </Link>
          </div>
          <ul className="kid-strip">
            {profiles.map((p) => (
              <li key={p.id}>
                <span className="kid-chip">
                  {p.name}
                  {p.is_main && <em>{t('kids.main')}</em>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card">
        <div className="card-head">
          <h2>{t('subscription.title')}</h2>
          <Link className="btn ghost" to="/subscription">
            {t('overview.viewSubscription')}
            <Icons.Chevron size={16} />
          </Link>
        </div>
        <p className="muted">
          {sub.state === 'active'
            ? t('subscription.expiresOn', { date: formatDate(sub.expiresAt) })
            : sub.state === 'expired'
              ? t('subscription.expiredOn', { date: formatDate(sub.expiresAt) })
              : t('subscription.noneBody')}
        </p>
      </section>
    </>
  );
}
